import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { signOut, type User } from "firebase/auth";

import type { DenormalizedPermission } from "@/apps/admin/utilities/data";
import { auth } from "@/common/platform/firebase";

// What Authenticated keeps of Firebase's user: plain fields, so the store stays serialisable.
export type AuthenticatedUser = Pick<
  User,
  "displayName" | "email" | "emailVerified" | "photoURL" | "uid" | "isAnonymous"
>;

export type PermissionsStatus = "loading" | "loaded" | "error";

export type AuthenticationState = {
  user: AuthenticatedUser | null;
  userLoaded: boolean;
  dialogOpen: boolean;
  permissions: DenormalizedPermission[];
  permissionsStatus: PermissionsStatus;
};

type AuthenticationRoot = { authentication: AuthenticationState };

const initialState: AuthenticationState = {
  user: null,
  userLoaded: false,
  dialogOpen: false,
  permissions: [],
  permissionsStatus: "loading",
};

export const signout = createAsyncThunk("authentication/signout", async () => {
  try {
    await signOut(auth);
  } finally {
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  }
});

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    setUser: (
      state,
      { payload: user }: PayloadAction<AuthenticatedUser | null>
    ) => {
      // Another account (or none): the previous user's permissions must not linger
      if (state.user?.uid !== user?.uid) {
        state.permissions = [];
        state.permissionsStatus = "loading";
      }
      state.user = user;
      state.userLoaded = true;
    },
    setPermissions: (
      state,
      {
        payload: { permissions },
      }: PayloadAction<{ permissions: DenormalizedPermission[] }>
    ) => {
      state.permissions = permissions;
      state.permissionsStatus = "loaded";
    },
    setPermissionsError: (state) => {
      state.permissions = [];
      state.permissionsStatus = "error";
    },
    openSigninDialog: (state) => {
      state.dialogOpen = true;
    },
    closeSigninDialog: (state) => {
      state.dialogOpen = false;
    },
  },
});

export const {
  setUser,
  setPermissions,
  setPermissionsError,
  openSigninDialog,
  closeSigninDialog,
} = authenticationSlice.actions;

export const selectUser = (state: AuthenticationRoot) =>
  state.authentication.user;
export const selectUserLoaded = (state: AuthenticationRoot) =>
  state.authentication.userLoaded;
export const selectPermissionsStatus = (state: AuthenticationRoot) =>
  state.authentication.permissionsStatus;
export const selectSignInDialogOpen = (state: AuthenticationRoot) =>
  state.authentication.dialogOpen;
export const selectPermissions = (state: AuthenticationRoot) =>
  state.authentication.permissions;

export default authenticationSlice.reducer;
